import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { ApiClient } from '../../../core/http/api-client';

export interface User {
  userId: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  isBlocked: boolean;
  deactivated: boolean;
  tenantName?: string;
  tenantId?: string;
  isTenantAdmin?: boolean;
  tenants?: UserTenantSummary[];
}

export interface UserTenantSummary {
  tenantId: string;
  tenantName: string;
  isTenantAdmin: boolean;
}

/**
 * Service for managing user operations.
 * Provides cached access to user data to reduce API calls.
 */
@Injectable({
  providedIn: 'root',
})
export class UserService {
  private api = inject(ApiClient);

  private usersCache = new Map<string, Observable<User[]>>();

  /**
   * Gets all visible users across all tenants the caller has access to.
   */
  getAllVisibleUsers(forceRefresh: boolean = false): Observable<User[]> {
    const cacheKey = '__all__';

    if (forceRefresh) {
      this.usersCache.delete(cacheKey);
    }

    if (!this.usersCache.has(cacheKey)) {
      const observable = this.api
        .get<User[]>('/Manager/User/list')
        .pipe(shareReplay(1));

      this.usersCache.set(cacheKey, observable);
    }

    return this.usersCache.get(cacheKey)!;
  }

  /**
   * Gets users by tenant ID with caching.
   */
  getUsersByTenantId(tenantId: string, forceRefresh: boolean = false): Observable<User[]> {
    const cacheKey = `tenant_${tenantId}`;

    if (forceRefresh) {
      this.usersCache.delete(cacheKey);
    }

    if (!this.usersCache.has(cacheKey)) {
      const observable = this.api
        .get<User[]>(`/Manager/User/Tenant/${tenantId}`)
        .pipe(shareReplay(1));

      this.usersCache.set(cacheKey, observable);
    }

    return this.usersCache.get(cacheKey)!;
  }

  /**
   * Clears user cache for a specific tenant or all tenants.
   */
  clearUsersCache(tenantId?: string): void {
    if (tenantId) {
      this.usersCache.delete(`tenant_${tenantId}`);
    }
    this.usersCache.delete('__all__');
  }

  getUserById(userId: string, tenantId: string): Observable<User> {
    return this.api.get<User>(`/Manager/User/${tenantId}/${userId}`);
  }

  createUser(user: Partial<User>): Observable<User | string> {
    return this.api.post<User | string>('/Manager/UserManagement', user);
  }

  updateUser(user: Partial<User>): Observable<User> {
    return this.api.put<User>('/Manager/UserManagement', user);
  }

  deleteUser(userId: string): Observable<void> {
    return this.api.delete<void>(`/Manager/UserManagement/${userId}`);
  }

  blockUser(userId: string, isBlocked: boolean): Observable<void> {
    return this.api.patch<void>(`/Manager/UserManagement/Block/${userId}`, { isBlocked });
  }

  changePassword(userId: string, password: string, tenantId?: string): Observable<void> {
    const payload: { userId: string; password: string; tenantId?: string } = { userId, password };
    if (tenantId) {
      payload.tenantId = tenantId;
    }
    return this.api.patch<void>('/Manager/UserManagement/Password', payload);
  }

  addUserToTenant(tenantId: string, userId: string): Observable<void> {
    return this.api.post<void>(`/Manager/UserManagement/Tenant/${tenantId}/User/${userId}`, {});
  }

  removeUserFromTenant(tenantId: string, userId: string): Observable<void> {
    return this.api.delete<void>(`/Manager/UserManagement/Tenant/${tenantId}/User/${userId}`);
  }
}
